import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { sortQuoteLineItems, type QuoteRow } from "./useQuotes";
import type { PipelineStage } from "@/types/quotes";

export interface QuoteFilters {
  search: string;
  stages: PipelineStage[];
  serviceType: string | null;
  eventTypeId: string | null;
  dateFrom: string | null;
  dateTo: string | null;
  hasValue: boolean | null;
  /** Exact `quotes.utm_source` value (#811) */
  utmSource: string | null;
  /** Exact `quotes.utm_campaign` value (#811) */
  utmCampaign: string | null;
}

export interface QuoteSortConfig {
  column: string;
  ascending: boolean;
}

export const DEFAULT_FILTERS: QuoteFilters = {
  search: "",
  stages: [],
  serviceType: null,
  eventTypeId: null,
  dateFrom: null,
  dateTo: null,
  hasValue: null,
  utmSource: null,
  utmCampaign: null,
};

export const DEFAULT_SORT: QuoteSortConfig = { column: "created_at", ascending: false };

interface UseFilteredQuotesOptions {
  filters: QuoteFilters;
  sort: QuoteSortConfig;
  page: number;
  pageSize: number;
  enabled?: boolean;
  /** Pipeline only (#1005): "open" = not lost and not won; "closed" = lost or won. */
  pipelineSet?: "open" | "closed";
}

export interface FilteredQuotesResult {
  quotes: QuoteRow[];
  totalCount: number;
}

export function useFilteredQuotes({ filters, sort, page, pageSize, enabled = true, pipelineSet }: UseFilteredQuotesOptions) {
  return useQuery({
    queryKey: ["quotes", "filtered", filters, sort, page, pageSize, pipelineSet ?? "all"],
    enabled,
    queryFn: async (): Promise<FilteredQuotesResult> => {
      // Build query
      let query = supabase
        .from("quotes")
        .select(
          "*, event_type:event_types(id, name), line_items:quote_line_items(*), latest_payment:quote_payments(payment_date, amount, method), created_by_profile:profiles!quotes_created_by_fkey(display_name), updated_by_profile:profiles!quotes_updated_by_fkey(display_name), contact:contacts(ghl_contact_id, ghl_location_id, last_message_at)",
          { count: "exact" }
        );

      // Apply filters
      if (filters.search) {
        query = query.or(
          `client_name.ilike.%${filters.search}%,quote_number.ilike.%${filters.search}%,client_email.ilike.%${filters.search}%`
        );
      }

      if (filters.stages.length > 0) {
        query = query.in("pipeline_stage", filters.stages);
      }

      if (filters.serviceType) {
        // service_type is the source of truth, but pre-#873 /order pickups are
        // drop_off + fulfillment_type 'pickup' (see effectiveServiceType).
        query = filters.serviceType === "pickup"
          ? query.or("service_type.eq.pickup,fulfillment_type.eq.pickup")
          : query.eq("service_type", filters.serviceType).or("fulfillment_type.is.null,fulfillment_type.neq.pickup");
      }

      if (filters.eventTypeId) {
        query = query.eq("event_type_id", filters.eventTypeId);
      }

      if (filters.dateFrom) {
        query = query.gte("event_date", filters.dateFrom);
      }

      if (filters.dateTo) {
        query = query.lte("event_date", filters.dateTo);
      }

      if (filters.hasValue === true) {
        query = query.gt("total", 0);
      } else if (filters.hasValue === false) {
        query = query.eq("total", 0);
      }

      if (filters.utmSource) {
        query = query.eq("utm_source", filters.utmSource);
      }

      if (filters.utmCampaign) {
        query = query.eq("utm_campaign", filters.utmCampaign);
      }

      if (pipelineSet === "open") {
        query = query.eq("is_lost", false).neq("pipeline_stage", "won");
      } else if (pipelineSet === "closed") {
        // A second .or() is ANDed with the search .or() (supabase-js appends params).
        query = query.or("is_lost.eq.true,pipeline_stage.eq.won");
      }

      // Sorting
      query = query.order(sort.column, { ascending: sort.ascending });

      // Payment ordering for latest_payment
      query = query
        .order("payment_date", { referencedTable: "quote_payments", ascending: false })
        .limit(1, { referencedTable: "quote_payments" });

      // Pagination
      const from = page * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;
      if (error) throw error;

      return {
        quotes: ((data ?? []) as QuoteRow[]).map(sortQuoteLineItems),
        totalCount: count ?? 0,
      };
    },
    // Same reasoning as useQuotes() — the filtered pipeline view is the
    // staff's work surface and should reflect recent edits, not a 30s
    // cached snapshot. Overrides the #146 global `refetchOnMount: "stale"`.
    refetchOnMount: "always",
  });
}

/** Count of active filters (excluding search) for badge display */
export function countActiveFilters(filters: QuoteFilters): number {
  let count = 0;
  if (filters.stages.length > 0) count++;
  if (filters.serviceType) count++;
  if (filters.eventTypeId) count++;
  if (filters.dateFrom || filters.dateTo) count++;
  if (filters.hasValue !== null) count++;
  if (filters.utmSource) count++;
  if (filters.utmCampaign) count++;
  return count;
}

/**
 * Concatenate the pipeline's open and closed result sets (#1005). Undefined until
 * either loads. The two queries run separately, so a quote that closes between
 * them can come back in both; it's kept once (the open copy) so no card repeats.
 */
export function mergePipelineResults(
  open: FilteredQuotesResult | undefined,
  closed: FilteredQuotesResult | undefined,
): FilteredQuotesResult | undefined {
  if (!open && !closed) return undefined;
  const openQuotes = open?.quotes ?? [];
  const seen = new Set(openQuotes.map((q) => q.id));
  return {
    quotes: [...openQuotes, ...(closed?.quotes ?? []).filter((q) => !seen.has(q.id))],
    totalCount: (open?.totalCount ?? 0) + (closed?.totalCount ?? 0),
  };
}

/** True when any filter, including search, is set */
export function hasActiveFilters(filters: QuoteFilters): boolean {
  return !!filters.search || countActiveFilters(filters) > 0;
}

export interface QuoteAttributionOptions {
  sources: string[];
  campaigns: string[];
}

interface AttributionRow {
  utm_source: string | null;
  utm_campaign: string | null;
}

function distinctNonBlank(values: (string | null)[]): string[] {
  // Blank values are dropped (a Radix SelectItem can't take value=""), but
  // real values stay untrimmed so the option equals the stored value and
  // the `.eq` filter matches it.
  const set = new Set(values.filter((v): v is string => !!v && v.trim() !== ""));
  return [...set].sort((a, b) => a.localeCompare(b));
}

export function distinctAttributionValues(rows: AttributionRow[]): QuoteAttributionOptions {
  return {
    sources: distinctNonBlank(rows.map((r) => r.utm_source)),
    campaigns: distinctNonBlank(rows.map((r) => r.utm_campaign)),
  };
}

/**
 * Ad source / campaign values for the quotes filter dropdowns (#811), read
 * from the attribution already stored on quotes. Capped at the 1000 most
 * recent attributed quotes (PostgREST's default row limit); a selected value
 * outside that window is still rendered by the filter bar.
 */
export function useQuoteAttributionOptions() {
  return useQuery({
    // Under ["quotes"] so the existing quote invalidations refresh it too.
    queryKey: ["quotes", "attribution-options"],
    queryFn: async (): Promise<QuoteAttributionOptions> => {
      const { data, error } = await supabase
        .from("quotes")
        .select("utm_source, utm_campaign")
        .or("utm_source.not.is.null,utm_campaign.not.is.null")
        .order("created_at", { ascending: false })
        .limit(1000);
      if (error) throw error;
      return distinctAttributionValues(data ?? []);
    },
  });
}
